import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <i className="ri-emotion-sad-line text-5xl text-foreground-600"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-200 mb-2 font-heading">
            电影未找到
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            该电影可能已被移除或链接无效
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === movie.category);
  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="pt-20 pb-8 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  {
                    label: category?.name || '全部',
                    path: `/category/${movie.category}`,
                  },
                  { label: movie.title },
                ]}
              />

              <div className="flex flex-col md:flex-row gap-8 mt-4">
                <div className="md:w-72 shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={movie.image}
                      alt={`${movie.title} 电影海报`}
                      title={`${movie.title} 高清电影海报`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-4xl font-bold text-foreground-100 font-heading mb-3">
                    {movie.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-full whitespace-nowrap">
                      {movie.quality}
                    </span>
                    <span className="px-3 py-1 text-xs font-medium bg-accent-500/80 text-background-950 rounded-full whitespace-nowrap">
                      {category?.name}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-accent-400">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-xs"></i>
                      </div>
                      {movie.rating}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 mb-6 text-sm">
                    <div>
                      <span className="text-foreground-600">导演：</span>
                      <span className="text-foreground-200">{movie.director}</span>
                    </div>
                    <div>
                      <span className="text-foreground-600">年份：</span>
                      <span className="text-foreground-200">{movie.year}</span>
                    </div>
                    <div>
                      <span className="text-foreground-600">片长：</span>
                      <span className="text-foreground-200">{movie.duration}</span>
                    </div>
                    <div className="col-span-2 sm:col-span-3">
                      <span className="text-foreground-600">主演：</span>
                      <span className="text-foreground-200">
                        {movie.actors.join('、')}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-foreground-300 mb-2 font-heading">
                      剧情简介
                    </h3>
                    <div
                      className={`text-sm text-foreground-500 leading-relaxed ${
                        !isDescriptionExpanded ? 'line-clamp-3' : ''
                      }`}
                    >
                      {movie.description}
                    </div>
                    {movie.description.length > 100 && (
                      <button
                        onClick={() =>
                          setIsDescriptionExpanded(!isDescriptionExpanded)
                        }
                        className="text-sm text-primary-400 hover:text-primary-300 mt-1 transition-colors"
                      >
                        {isDescriptionExpanded ? '收起' : '展开全部'}
                      </button>
                    )}
                  </div>

                  <div className="mt-8 flex items-center gap-3">
                    <a
                      href="#"
                      className="flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-play-fill text-lg"></i>
                      </div>
                      立即播放
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {relatedMovies.length > 0 && (
          <section className="py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard
                      key={m.id}
                      id={m.id}
                      title={m.title}
                      year={m.year}
                      rating={m.rating}
                      image={m.image}
                      quality={m.quality}
                      categoryName={category?.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.image,
            dateCreated: movie.year.toString(),
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.actors.map((actor) => ({
              '@type': 'Person',
              name: actor,
            })),
            duration: movie.duration,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating.toString(),
              bestRating: '10',
            },
          }),
        }}
      />
    </div>
  );
}